import React, { useEffect, useMemo, useState } from 'react';
import { CircularProgress, Menu, MenuItem, Snackbar } from '@mui/material';
import { EllipsisVertical, Download, TableProperties, LayoutGrid, Presentation, FileDown } from 'lucide-react';
import dayjs from 'dayjs';
import pptxgen from 'pptxgenjs';
import { LOCAL_STORAGE } from '../../globals/globals';
import KpiCard from './KpiCard';
import { exportKpiSummaryCardPdf } from './exportPagePdf';
import { getAdaptiveScaleMin, getObservedKpiMin } from './kpiScale';

const LTE_KPI_FIELDS = [
  { key: 'dataAccessibilityPercent',        label: 'Data Accessibility' },
  { key: 'volteAccessibilityPercent',       label: 'VoLTE Accessibility' },
  { key: 'dataRetainabilityPercent',        label: 'Data Retainability' },
  { key: 'volteRetainabilityPercent',       label: 'VoLTE Retainability' },
  { key: 's1SuccessRatePercent',            label: 'S1 Success Rate' },
  { key: 'macroApHandinPercent',            label: 'Macro AP Hand-in' },
  { key: 'apMacroIntraHandoutPercent',      label: 'AP Macro Intra Freq HO' },
  { key: 'apMacroInterFreqHandoutPercent',  label: 'AP Macro Inter Freq HO' },
  { key: 'intraEranInterFreqPercent',       label: 'Intra ERAN Inter Freq' },
  { key: 'intraEranIntraFreqPercent',       label: 'Intra ERAN Intra Freq' },
  { key: 'x1HoIntraFreqPercent',            label: 'X1 HO Intra Freq' },
  { key: 'x1HoInterFreqPercent',            label: 'X1 HO Inter Freq' },
];

const NR_TABLE_KPI_FIELDS = [
  { key: 'endcDataAccessibilityPercent',       label: 'ENDC Data Accessibility' },
  { key: 'endcDataRetainabilityPercent',       label: 'ENDC Data Retainability' },
  { key: 'endcSgNbAdditionSuccessRatePercent', label: 'SgNB Addition SR' },
  { key: 'endcIntraCuIntraFreqHoSrPercent',   label: 'Intra CU Intra-Freq HO SR' },
];

// Per-KPI scale and target thresholds for the card progress bars
const LTE_KPI_CARDS = [
  { title: 'Data Accessibility',        field: 'dataAccessibilityPercent',       scaleMin: 98.5, target: 99.5 },
  { title: 'VoLTE Accessibility',       field: 'volteAccessibilityPercent',      scaleMin: 98.5, target: 99.5 },
  { title: 'Data Retainability',        field: 'dataRetainabilityPercent',       scaleMin: 98.5, target: 99.5 },
  { title: 'VoLTE Retainability',       field: 'volteRetainabilityPercent',      scaleMin: 98.5, target: 99.5 },
  { title: 'S1 Success Rate',           field: 's1SuccessRatePercent',           scaleMin: 99.0, target: 99.7 },
  { title: 'Macro AP Hand-in',          field: 'macroApHandinPercent',           scaleMin: 97.0, target: 98.5 },
  { title: 'AP Macro Intra Freq HO',    field: 'apMacroIntraHandoutPercent',     scaleMin: 98.5, target: 99.5 },
  { title: 'AP Macro Inter Freq HO',    field: 'apMacroInterFreqHandoutPercent', scaleMin: 98.5, target: 99.5 },
  { title: 'Intra ERAN Inter Freq',     field: 'intraEranInterFreqPercent',      scaleMin: 98.5, target: 99.5 },
  { title: 'Intra ERAN Intra Freq',     field: 'intraEranIntraFreqPercent',      scaleMin: 98.5, target: 99.5 },
  { title: 'X1 HO Intra Freq',          field: 'x1HoIntraFreqPercent',           scaleMin: 98.5, target: 99.5 },
  { title: 'X1 HO Inter Freq',          field: 'x1HoInterFreqPercent',           scaleMin: 98.0, target: 99.0 },
];

const NR_KPI_CARDS = [
  { title: 'ENDC Data Accessibility',   field: 'endcDataAccessibilityPercent',       scaleMin: 97.0, target: 98.5 },
  { title: 'ENDC Data Retainability',   field: 'endcDataRetainabilityPercent',       scaleMin: 97.0, target: 98.5 },
  { title: 'ENDC SgNB Addition SR',     field: 'endcSgNbAdditionSuccessRatePercent', scaleMin: 97.0, target: 98.5 },
  { title: 'Intra CU Intra Freq HO SR', field: 'endcIntraCuIntraFreqHoSrPercent',   scaleMin: 97.0, target: 98.5 },
];

interface MonthSlice {
  label: string;
  shortLabel: string;
  start: string;
  end: string;
}

type KpiValueMap = Record<string, number | null>;

/**
 * Returns the 3 months of the prior completed quarter as individual monthly
 * sparkline buckets. e.g. if today is in Q3, returns Jan, Feb, Mar (Q1 months).
 * Oldest first.
 */
function getPriorQuarterMonths(): MonthSlice[] {
  const now = dayjs();
  const currentQStart = now.startOf('year').add(Math.floor(now.month() / 3) * 3, 'month');
  const priorQStart = currentQStart.subtract(3, 'month');
  return [0, 1, 2].map((monthOffset) => {
    const start = priorQStart.add(monthOffset, 'month').startOf('month');
    const end   = start.endOf('month');
    return {
      label:      start.format('MMMM YYYY'),
      shortLabel: start.format('MMM'),
      start: start.format('YYYY-MM-DD'),
      end:   end.format('YYYY-MM-DD'),
    };
  });
}

/**
 * Returns 3 bi-monthly (2-month) buckets covering the prior 2 completed
 * quarters. e.g. if today is in Q3, covers Q1+Q2 = Jan–Jun, split as:
 * Jan–Feb, Mar–Apr, May–Jun. Oldest first.
 */
function getPriorTwoQuartersBiMonthly(): MonthSlice[] {
  const now = dayjs();
  const currentQStart = now.startOf('year').add(Math.floor(now.month() / 3) * 3, 'month');
  const twoQStart = currentQStart.subtract(6, 'month');
  return [0, 1, 2].map((bucketIndex) => {
    const start = twoQStart.add(bucketIndex * 2, 'month').startOf('month');
    const end   = start.add(1, 'month').endOf('month');
    return {
      label:      `${start.format('MMM')}–${end.format('MMM YYYY')}`,
      shortLabel: start.format('MMM'),
      start: start.format('YYYY-MM-DD'),
      end:   end.format('YYYY-MM-DD'),
    };
  });
}

/**
 * Returns sparkline periods for the given timeRange.
 * Only quarter modes produce sparkline data; rolling windows do not.
 */
function getSparklineMonths(timeRange: string): MonthSlice[] {
  if (timeRange === 'lastQuarter') return getPriorQuarterMonths();
  if (timeRange === 'lastTwoQuarters') return getPriorTwoQuartersBiMonthly();
  return [];
}

/**
 * Returns the start_date / end_date for the main KPI summary fetch.
 * Rolling modes use time_range; quarter modes use explicit date boundaries.
 */
function getKpiDateRange(timeRange: string): { time_range?: string; start_date?: string; end_date?: string } {
  const now = dayjs();
  if (timeRange === 'lastQuarter') {
    const currentQStart = now.startOf('year').add(Math.floor(now.month() / 3) * 3, 'month');
    const priorQStart = currentQStart.subtract(3, 'month');
    return {
      start_date: priorQStart.format('YYYY-MM-DD'),
      end_date:   currentQStart.subtract(1, 'day').format('YYYY-MM-DD'),
    };
  }
  if (timeRange === 'lastTwoQuarters') {
    const currentQStart = now.startOf('year').add(Math.floor(now.month() / 3) * 3, 'month');
    const twoQStart = currentQStart.subtract(6, 'month');
    return {
      start_date: twoQStart.format('YYYY-MM-DD'),
      end_date:   currentQStart.subtract(1, 'day').format('YYYY-MM-DD'),
    };
  }
  return { time_range: timeRange };
}

/**
 * Average KPI values across rows while ignoring missing/non-numeric values.
 * Returns null for a KPI when no valid value exists for that month.
 */
function averageRows(rows: any[], fieldKeys: { key: string }[]): KpiValueMap {
  const result: KpiValueMap = {};
  for (const f of fieldKeys) {
    const values = rows
      .map((r) => Number(r?.[f.key]))
      .filter((v) => Number.isFinite(v));
    if (!values.length) {
      result[f.key] = null;
      continue;
    }
    const avg = values.reduce((acc, v) => acc + v, 0) / values.length;
    result[f.key] = parseFloat(avg.toFixed(2));
  }
  return result;
}

// ── PPTx helper: draw a single KPI card using shapes ───────────────────────
function addKpiCardToSlide(
  slide: any,
  x: number, y: number, w: number, h: number,
  cardTitle: string, value: number, unit: string,
  scaleMin: number, scaleMax: number, target: number,
  delta?: number,
  trendLabels?: string[],
  trendValues?: number[],
) {
  const CARD_BG        = 'F8FAFC';
  const CARD_BORDER    = 'E2E8F0';
  const MUTED_COLOR    = '94A3B8';
  const FG_COLOR       = '1E293B';
  const observedMin    = getObservedKpiMin(value, trendValues);
  const effectiveScaleMin = getAdaptiveScaleMin(observedMin, scaleMin);
  const aboveTarget    = value >= target;
  const range          = Math.max(0.0001, scaleMax - effectiveScaleMin);
  const valuePct       = Math.min(1, Math.max(0, (value - effectiveScaleMin) / range));
  const targetPct      = Math.min(1, Math.max(0, (target - effectiveScaleMin) / range));

  // Card background
  slide.addShape('rect' as any, {
    x, y, w, h,
    fill: { color: CARD_BG },
    line: { color: CARD_BORDER, width: 0.75 },
  });

  // Title
  slide.addText(cardTitle.toUpperCase(), {
    x: x + 0.1, y: y + 0.07, w: w - 0.2, h: 0.18,
    fontSize: 6.5, bold: true, color: MUTED_COLOR, align: 'left',
  });

  // Value
  slide.addText(`${value.toFixed(2)}${unit}`, {
    x: x + 0.1, y: y + 0.24, w: w - 0.2, h: 0.42,
    fontSize: 20, bold: true, color: FG_COLOR, align: 'left',
  });

  // Progress bar
  const barY = y + 0.78;
  const barH = 0.09;
  const barX = x + 0.15;
  const barW = w - 0.3;
  const trackColor = aboveTarget ? '2e5670' : 'a25423';

  // Track
  slide.addShape('rect' as any, {
    x: barX, y: barY, w: barW, h: barH,
    fill: { color: trackColor },
    line: { color: trackColor, width: 0 },
  });

  // Fill
  if (valuePct > 0) {
    slide.addShape('rect' as any, {
      x: barX, y: barY, w: Math.max(0.01, barW * valuePct), h: barH,
      fill: { color: aboveTarget ? '00d995' : 'f96381' },
      line: { color: aboveTarget ? '00d995' : 'f96381', width: 0 },
    });
  }

  // Target marker
  slide.addShape('rect' as any, {
    x: barX + barW * targetPct - 0.01, y: barY - 0.04, w: 0.02, h: barH + 0.08,
    fill: { color: FG_COLOR },
    line: { color: FG_COLOR, width: 0 },
  });

  // Scale labels
  const labelY = barY + barH + 0.04;
  slide.addText(`${effectiveScaleMin}${unit}`, {
    x: barX, y: labelY, w: 0.5, h: 0.13,
    fontSize: 6, color: MUTED_COLOR, align: 'left',
  });
  slide.addText(`${scaleMax}${unit}`, {
    x: barX + barW - 0.5, y: labelY, w: 0.5, h: 0.13,
    fontSize: 6, color: MUTED_COLOR, align: 'right',
  });
  // Target label
  const tgtLabelX = barX + barW * targetPct;
  slide.addText(`${target} tgt`, {
    x: Math.max(barX, Math.min(barX + barW - 0.6, tgtLabelX - 0.3)),
    y: labelY, w: 0.6, h: 0.13,
    fontSize: 6, color: MUTED_COLOR, align: 'center',
  });

  // Sparkline (mini quarterly trend line chart)
  const hasTrend = trendLabels && trendValues && trendValues.length >= 2 && h >= 1.6;
  if (hasTrend) {
    const sparkY = y + 1.12;
    const sparkH = Math.max(0.4, h - 1.12 - 0.22);
    slide.addChart('line' as any, [{ name: 'Trend', labels: trendLabels, values: trendValues }], {
      x: barX, y: sparkY, w: barW, h: sparkH,
      showValue: false,
      valAxisMinVal: effectiveScaleMin,
      valAxisMaxVal: scaleMax,
      chartColors: ['a855f7'],
      showLegend: false,
      catAxisLabelFontSize: 6.5,
      valAxisHidden: true,
      lineDataSymbol: 'circle',
      lineDataSymbolSize: 3,
      plotAreaBorderColor: CARD_BG,
    } as any);
  }

  // Delta (below sparkline if present, otherwise below scale labels)
  if (delta !== undefined) {
    const deltaColor = delta >= 0 ? '33b891' : 'c3b051';
    const deltaY = hasTrend ? y + h - 0.20 : labelY + 0.16;
    slide.addText(`Q Delta: ${delta >= 0 ? '+' : ''}${delta.toFixed(2)}${unit}`, {
      x: barX, y: deltaY, w: barW, h: 0.14,
      fontSize: 7, color: deltaColor, align: 'right', bold: true,
    });
  }
}

interface KpiSummaryBarChartProps {
  timeRange: string;
  regions?: string[];
  totalRegionCount?: number;
  variant?: 'lte' | 'nr-table';
  refreshTrigger?: number;
}

// ── Main component ──────────────────────────────────────────────
const KpiSummaryBarChart: React.FC<KpiSummaryBarChartProps> = ({ timeRange, regions, totalRegionCount, variant = 'lte', refreshTrigger }) => {
  const fields = variant === 'nr-table' ? NR_TABLE_KPI_FIELDS : LTE_KPI_FIELDS;
  const kpiCards = variant === 'nr-table' ? NR_KPI_CARDS : LTE_KPI_CARDS;
  const apiEndpoint = variant === 'nr-table'
    ? '/v1/charts/api/nr/nr-table-kpi-summary'
    : '/v1/charts/api/lte/kpi-summary-by-region';
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const storageKey = `kpi-summary-view-${variant}`;
  const [showTable, setShowTable] = useState<boolean>(() => sessionStorage.getItem(storageKey) === 'true');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [pptxLoading, setPptxLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [trendMonthData, setTrendMonthData] = useState<(KpiValueMap | null)[]>([]);
  const trendMonths = useMemo(() => getSparklineMonths(timeRange), [timeRange, refreshTrigger]);

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleExportPdf = () => {
    handleMenuClose();
    if (!data.length) return;
    setPdfLoading(true);
    try {
      const exportMonthData: (Record<string, number> | null)[] = trendMonthData.map((month) => {
        if (!month) return null;
        const normalized: Record<string, number> = {};
        Object.entries(month).forEach(([k, v]) => {
          normalized[k] = Number.isFinite(v as number) ? Number(v) : 0;
        });
        return normalized;
      });
      exportKpiSummaryCardPdf({
        data,
        kpiCards,
        variant,
        timeRange,
        monthLabels: trendMonths.map(m => m.shortLabel),
        monthData: exportMonthData,
      });
    } finally {
      setPdfLoading(false);
    }
  };

  const handleToggleView = () => {
    const next = !showTable;
    setShowTable(next);
    sessionStorage.setItem(storageKey, String(next));
    handleMenuClose();
  };

  const csvFilename = variant === 'nr-table' ? 'kpi_summary_nr' : 'kpi_summary_lte';

  const handleExportCSV = () => {
    if (!data.length) return;
    const headers = ['Region', ...fields.map(f => f.label)];
    const rows = data.map(row => [row.region, ...fields.map(f => parseFloat((row[f.key] ?? 0).toFixed(2)))]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${csvFilename}_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.csv`;
    link.click();
    handleMenuClose();
  };

  const handleExportPptx = async () => {
    if (!regions || regions.length === 0) return;
    handleMenuClose();
    setPptxLoading(true);
    const variantLabel = variant === 'nr-table' ? 'NR KPI Summary' : 'LTE KPI Summary';
    const timeLabel = (() => {
      const map: Record<string, string> = {
        lastWeek:        'Last 7-days',
        lastMonth:       'Last 30-days',
        lastQuarter:     'Prior Quarter',
        lastTwoQuarters: 'Prior 2 Quarters',
      };
      return map[timeRange] ?? timeRange;
    })();

    // Fetch one row per region individually
    const token = localStorage.getItem(LOCAL_STORAGE.TOKEN);
    const perRegionData: any[] = [];
    for (const regionName of regions) {
      try {
        const kpiDateRange = getKpiDateRange(timeRange);
        const params = new URLSearchParams({ ...kpiDateRange, region: regionName });
        const res = await fetch(`${apiEndpoint}?${params}`, {
          credentials: 'include',
          headers: { 'Accept': 'application/json', ...(token && { 'Authorization': `Bearer ${token}` }) },
        });
        if (res.ok) {
          const rows: any[] = await res.json();
          if (rows.length > 0) perRegionData.push(rows[0]);
        }
      } catch { /* skip failed region */ }
    }
    if (!perRegionData.length) { setPptxLoading(false); return; }

    try {
    const pptx = new pptxgen();
    pptx.layout = 'LAYOUT_WIDE'; // 13.33" x 7.5"
    pptx.title = variantLabel;

    // ── Cover slide ──────────────────────────────────────────────────────────
    const cover = pptx.addSlide();
    cover.background = { color: '1E293B' };
    cover.addText(variantLabel, {
      x: 0.5, y: 2.5, w: 12.33, h: 1,
      fontSize: 36, bold: true, color: 'FFFFFF', align: 'center',
    });
    cover.addText(`Time Range: ${timeLabel}  |  Generated: ${dayjs().format('YYYY-MM-DD HH:mm')}`, {
      x: 0.5, y: 3.7, w: 12.33, h: 0.5,
      fontSize: 14, color: '94A3B8', align: 'center',
    });
    cover.addText(`Regions: ${perRegionData.map(r => r.region).join(', ')}`, {
      x: 0.5, y: 4.3, w: 12.33, h: 0.5,
      fontSize: 12, color: '94A3B8', align: 'center',
    });

    // ── One slide per region ─────────────────────────────────────────────────
    for (const regionRow of perRegionData) {
      const slide = pptx.addSlide();
      slide.background = { color: 'FFFFFF' };

      // Title bar
      slide.addShape('rect' as any, {
        x: 0, y: 0, w: '100%', h: 0.6,
        fill: { color: '1E293B' },
      });
      slide.addText(`${variantLabel}  —  ${regionRow.region}`, {
        x: 0.3, y: 0.05, w: 9, h: 0.5,
        fontSize: 14, bold: true, color: 'FFFFFF',
      });
      slide.addText(timeLabel, {
        x: 9.3, y: 0.05, w: 3.7, h: 0.5,
        fontSize: 11, color: '94A3B8', align: 'right',
      });

      // KPI card grid
      const numCols = 4;
      const numRows = Math.ceil(kpiCards.length / numCols);
      const GRID_X = 0.3;
      const GRID_Y = 0.75;
      const GAP_X  = 0.12;
      const GAP_Y  = 0.12;
      const gridW  = 13.33 - GRID_X * 2;
      const gridH  = 7.5 - GRID_Y - 0.1;
      const cardW     = (gridW - GAP_X * (numCols - 1)) / numCols;
      const rawCardH  = (gridH - GAP_Y * (numRows - 1)) / numRows;
      const cardH     = Math.min(rawCardH, 2.5);
      const safePptxMonthData = trendMonthData.slice(0, trendMonths.length);

      kpiCards.forEach(({ title: cardTitle, field, scaleMin, target }, idx) => {
        const col   = idx % numCols;
        const row   = Math.floor(idx / numCols);
        const cx    = GRID_X + col * (cardW + GAP_X);
        const cy    = GRID_Y + row * (cardH + GAP_Y);
        const value = parseFloat((regionRow[field] ?? 0).toFixed(2));
        const rawPoints = safePptxMonthData
          .map((d, i) => {
            const v = d?.[field];
            return Number.isFinite(v as number) ? { i, v: Number(v) } : null;
          })
          .filter((p): p is { i: number; v: number } => p !== null);
        const hasNonZero = rawPoints.some((p) => p.v > 0);
        const availableIdx = rawPoints
          .filter((p) => !hasNonZero || p.v > 0)
          .map((p) => p.i);
        const hasTrendData = availableIdx.length >= 2;
        const trendLbls = hasTrendData ? availableIdx.map(i => trendMonths[i].shortLabel) : undefined;
        const trendVals = hasTrendData
          ? availableIdx.map(i => Number(safePptxMonthData[i]?.[field]))
          : undefined;
        const firstIdx = availableIdx[0];
        const lastIdx  = availableIdx[availableIdx.length - 1];
        const firstVal = firstIdx !== undefined ? safePptxMonthData[firstIdx]?.[field] : null;
        const lastVal = lastIdx !== undefined ? safePptxMonthData[lastIdx]?.[field] : null;
        const delta = hasTrendData && Number.isFinite(firstVal as number) && Number.isFinite(lastVal as number)
          ? parseFloat((Number(lastVal) - Number(firstVal)).toFixed(2))
          : undefined;
        addKpiCardToSlide(slide, cx, cy, cardW, cardH, cardTitle, value, '%', scaleMin, 100, target, delta, trendLbls, trendVals);
      });
    }

    await pptx.writeFile({ fileName: `${csvFilename}_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.pptx` });
    } finally {
      setPptxLoading(false);
    }
  };

  useEffect(() => {
    // If all regions were explicitly deselected, don't fetch
    if (regions !== undefined && regions.length === 0) {
      setData([]);
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const kpiDateRange = getKpiDateRange(timeRange);
        const params = new URLSearchParams(kpiDateRange);
        const allSelected = !regions || regions.length === 0 || (totalRegionCount !== undefined && regions.length >= totalRegionCount);
        if (!allSelected) {
          regions!.forEach(r => params.append('region', r));
        }
        const token = localStorage.getItem(LOCAL_STORAGE.TOKEN);
        const response = await fetch(
          `${apiEndpoint}?${params}`,
          {
            credentials: 'include',
            headers: {
              'Accept': 'application/json',
              ...(token && { 'Authorization': `Bearer ${token}` }),
            },
          }
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        setData(await response.json());
      } catch (err: any) {
        setError(err.message || 'Failed to load');
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [timeRange, regions, totalRegionCount, variant, refreshTrigger]);

  // Fetch sparkline trend data: only for quarter modes (lastQuarter / lastTwoQuarters)
  useEffect(() => {
    if (trendMonths.length === 0 || (regions !== undefined && regions.length === 0)) {
      setTrendMonthData([]);
      return;
    }
    const fetchTrend = async () => {
      const token = localStorage.getItem(LOCAL_STORAGE.TOKEN);
      const allSelected = !regions || regions.length === 0 ||
        (totalRegionCount !== undefined && regions.length >= totalRegionCount);
      const results = await Promise.all(
        trendMonths.map(async (month) => {
          try {
            const params = new URLSearchParams({ start_date: month.start, end_date: month.end });
            if (!allSelected) regions!.forEach(r => params.append('region', r));
            const res = await fetch(`${apiEndpoint}?${params}`, {
              credentials: 'include',
              headers: {
                'Accept': 'application/json',
                ...(token && { 'Authorization': `Bearer ${token}` }),
              },
            });
            if (!res.ok) return null;
            const rows: any[] = await res.json();
            return rows.length ? averageRows(rows, fields) : null;
          } catch {
            return null;
          }
        })
      );
      setTrendMonthData(results);
    };
    fetchTrend();
  }, [timeRange, regions, totalRegionCount, variant, refreshTrigger]);

  // Average KPI values across all returned region rows for the card view
  const avgRow = useMemo<Record<string, number> | null>(() => {
    if (!data.length) return null;
    const avg: Record<string, number> = {};
    for (const f of fields) {
      const sum = data.reduce((acc, row) => acc + (parseFloat(row[f.key]) || 0), 0);
      avg[f.key] = parseFloat((sum / data.length).toFixed(2));
    }
    return avg;
  }, [data, fields]);

  const title = 'KPI Summary';

  return (
    <div
      className="rounded-xl border bg-card text-card-foreground shadow mt-4"
      style={{ padding: '16px 20px' }}
    >
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-base font-semibold text-foreground">{title}</h2>
        <button
          onClick={handleMenuOpen}
          className="h-8 w-8 flex items-center justify-center rounded-md bg-background text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          aria-label="chart options"
        >
          <EllipsisVertical className="h-4 w-4" />
        </button>
      </div>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: 'var(--color-popover, var(--popover))',
              color: 'var(--color-foreground, var(--foreground))',
              borderColor: 'var(--color-border, var(--border))',
              border: '1px solid',
            },
          },
        }}
      >
        <MenuItem onClick={handleToggleView} sx={{ color: 'inherit' }}>
          {showTable
            ? <LayoutGrid className="h-4 w-4 mr-2" />
            : <TableProperties className="h-4 w-4 mr-2" />}
          {showTable ? 'Show as Cards' : 'Show as Table'}
        </MenuItem>
        <MenuItem onClick={handleExportCSV} disabled={!data.length} sx={{ color: 'inherit' }}>
          <Download className="h-4 w-4 mr-2" />
          Export as CSV
        </MenuItem>
        <MenuItem onClick={handleExportPptx} disabled={!data.length || pptxLoading} sx={{ color: 'inherit' }}>
          {pptxLoading
            ? <CircularProgress size={14} thickness={4} sx={{ mr: '8px', color: 'inherit' }} />
            : <Presentation className="h-4 w-4 mr-2" />}
          {pptxLoading ? 'Generating…' : 'Export as PowerPoint'}
        </MenuItem>        <MenuItem onClick={handleExportPdf} disabled={pdfLoading} sx={{ color: 'inherit' }}>
          {pdfLoading
            ? <CircularProgress size={14} thickness={4} sx={{ mr: '8px', color: 'inherit' }} />
            : <FileDown className="h-4 w-4 mr-2" />}
          {pdfLoading ? 'Exporting\u2026' : 'Export as PDF'}
        </MenuItem>      </Menu>

      <Snackbar
        open={pptxLoading}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={
          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <CircularProgress size={16} thickness={4} sx={{ color: '#fff' }} />
            Generating PowerPoint… please wait
          </span>
        }
      />

      {loading ? (
        <div className="flex justify-center items-center h-56">
          <CircularProgress size={32} />
        </div>
      ) : error ? (
        <div className="flex justify-center items-center h-56 text-destructive text-sm">{error}</div>
      ) : !data.length && regions !== undefined && regions.length === 0 ? (
        <div className="flex justify-center items-center h-56 text-muted-foreground text-sm">Please select a region</div>
      ) : !data.length ? (
        <div className="flex justify-center items-center h-56 text-muted-foreground text-sm">No data available</div>
      ) : showTable ? (
        // Table view
        <div className="overflow-auto rounded-md border border-border" style={{ maxHeight: 400 }}>
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-foreground border-b border-border">Region</th>
                {fields.map((f, i) => (
                  <th key={i} className="px-3 py-2 text-left font-semibold text-foreground border-b border-border">{f.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i} className="hover:bg-accent/50 transition-colors">
                  <td className="px-3 py-2 text-foreground border-b border-border">{row.region}</td>
                  {fields.map((f, j) => (
                    <td key={j} className="px-3 py-2 text-foreground border-b border-border">
                      {parseFloat((row[f.key] ?? 0).toFixed(2))}%
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        // Cards view (default) — one KpiCard per KPI, values averaged across regions
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 xl:gap-3">
          {kpiCards.map(({ title: cardTitle, field, scaleMin, target }) => {
            // Use only per-field finite values so missing KPI values do not
            // get coerced to 0% and drag sparklines down.
            const safeMonthData = trendMonthData.slice(0, trendMonths.length);
            const rawPoints = safeMonthData
              .map((d, i) => {
                const v = d?.[field];
                return Number.isFinite(v as number) ? { i, v: Number(v) } : null;
              })
              .filter((p): p is { i: number; v: number } => p !== null);
            const hasNonZero = rawPoints.some((p) => p.v > 0);
            const availableIdx = rawPoints
              .filter((p) => !hasNonZero || p.v > 0)
              .map((p) => p.i);
            const hasTrend = availableIdx.length >= 2;
            const trendData = hasTrend
              ? availableIdx.map(i => ({
                  month: trendMonths[i].shortLabel,
                  value: Number(safeMonthData[i]?.[field]),
                }))
              : undefined;
            const firstIdx = availableIdx[0];
            const lastIdx  = availableIdx[availableIdx.length - 1];
            const firstVal = firstIdx !== undefined ? safeMonthData[firstIdx]?.[field] : null;
            const lastVal = lastIdx !== undefined ? safeMonthData[lastIdx]?.[field] : null;
            const delta = hasTrend && Number.isFinite(firstVal as number) && Number.isFinite(lastVal as number)
              ? parseFloat((Number(lastVal) - Number(firstVal)).toFixed(2))
              : undefined;
            return (
              <KpiCard
                key={cardTitle}
                title={cardTitle}
                value={avgRow ? (avgRow[field] ?? 0) : 0}
                scaleMin={scaleMin}
                target={target}
                sparklineEnabled={trendMonths.length > 0}
                trendData={trendData}
                delta={delta}
                deltaLabel="Q delta"
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default KpiSummaryBarChart;

